// @migration-in-progress
/**
 * Emergency Checklists Configuration
 * 
 * Pre-defined emergency response checklists that are automatically selected
 * based on vessel type, operations, and equipment configuration.
 * 
 * Each checklist contains:
 * - id: Unique identifier
 * - title: Display name
 * - type: Emergency type category
 * - icon: Lucide icon name for display
 * - priority: criticality level (critical/high/medium)
 * - applicableFor: Function to determine if checklist applies to vessel
 * - triggers: Vessel configurations that suggest this emergency type
 * - steps: Array of action items to complete
 * - notes: Additional guidance or references
 */

import { 
  Flame, 
  Anchor, 
  UserX, 
  Droplet, 
  Navigation, 
  Zap, 
  AlertTriangle,
  Wind,
  Heart,
  Shield,
  Activity,
  Truck,
  Radio,
  Waves,
  AlertCircle,
  Users
} from 'lucide-react';

export const EMERGENCY_CHECKLISTS = {
  version: 1,
  lastUpdated: new Date().toISOString(),
  checklists: [
    // FIRE EMERGENCY
    {
      id: 'fire_all_v1',
      title: 'Fire Response',
      type: 'fire',
      icon: Flame,
      vesselTypes: ['all'],
      sortOrder: 1,
      duration: '10-15 min',
      tags: ['fire', 'emergency'],
      steps: [
        { text: 'Sound fire alarm', required: true },
        { text: 'Identify source/location of fire', required: true },
        { text: 'Notify bridge/skipper', required: true },
        { text: 'Muster crew at designated station', required: true },
        { text: 'Use fire extinguisher or fixed system', required: true },
        { text: 'Shut down ventilation', condition: 'has_engine_spaces', required: false },
        { text: 'Call emergency services/DSC', condition: 'cat_0_4', required: true },
        { text: 'Consider evacuation if fire cannot be controlled', required: true },
        { text: 'Log time, actions taken, and outcome', required: true }
      ],
      reference: 'WBC3 Section 10.1.9'
    },

    // MAN OVERBOARD
    {
      id: 'mob_all_v1',
      title: 'Man Overboard',
      type: 'mob',
      icon: Users,
      vesselTypes: ['all'],
      sortOrder: 2,
      duration: '5-10 min',
      tags: ['mob', 'emergency'],
      steps: [
        { text: 'Alert crew - shout "Man Overboard"', required: true },
        { text: 'Deploy MOB marker/lifebuoy', required: true },
        { text: 'Sound general alarm', required: true },
        { text: 'Keep visual contact with casualty', required: true },
        { text: 'Stop engines if safe to do so', required: true },
        { text: 'Launch rescue craft', condition: 'has_rescue_boat', required: false },
        { text: 'Radio Mayday if required', condition: 'cat_0_4', required: true },
        { text: 'Record GPS position and time', required: true },
        { text: 'Log all actions and timeline', required: true }
      ],
      reference: 'WBC3 Section 10.1.10'
    },

    // COLLISION
    {
      id: 'collision_all_v1',
      title: 'Collision',
      type: 'collision',
      icon: AlertTriangle,
      vesselTypes: ['all'],
      sortOrder: 3,
      duration: '15-20 min',
      tags: ['collision', 'emergency'],
      steps: [
        { text: 'Sound general alarm', required: true },
        { text: 'Assess hull for damage', required: true },
        { text: 'Check for injuries', required: true },
        { text: 'Check watertight compartments', condition: 'length_over_10m', required: false },
        { text: 'Exchange details with other vessel', required: true },
        { text: 'Log time, position, speed, and conditions', required: true },
        { text: 'Notify authorities/company', required: true },
        { text: 'Take photos if safe', required: false },
        { text: 'Do not admit liability', required: true }
      ],
      reference: 'WBC3 Section 10.1.9, ISM Code'
    },

    // FLOODING
    {
      id: 'flooding_all_v1',
      title: 'Flooding/Water Ingress',
      type: 'flooding',
      icon: Droplet,
      vesselTypes: ['all'],
      sortOrder: 4,
      duration: '10-15 min',
      tags: ['flooding', 'emergency'],
      steps: [
        { text: 'Sound general alarm', required: true },
        { text: 'Identify source of water ingress', required: true },
        { text: 'Activate bilge pumps', required: true },
        { text: 'Isolate affected compartment', condition: 'has_compartments', required: false },
        { text: 'Check watertight doors', condition: 'has_watertight_doors', required: false },
        { text: 'Deploy damage control equipment', required: true },
        { text: 'Notify Coastguard', condition: 'cat_0_3', required: true },
        { text: 'Consider beaching if sinking', required: false },
        { text: 'Prepare for abandon ship', required: false },
        { text: 'Log actions and vessel status', required: true }
      ],
      reference: 'WBC3 Section 10.1.9'
    },

    // ABANDON SHIP
    {
      id: 'abandon_ship_v1',
      title: 'Abandon Ship',
      type: 'abandon',
      icon: Anchor,
      vesselTypes: ['all'],
      category: ['Category 0', 'Category 1', 'Category 2', 'Category 3'],
      sortOrder: 5,
      duration: '5-10 min',
      tags: ['abandon', 'emergency', 'cat0-3-specific'],
      steps: [
        { text: 'Sound abandon ship signal (7+ short, 1 long)', required: true },
        { text: 'Send distress signal (MAYDAY)', condition: 'cat_0_4', required: true },
        { text: 'Distribute lifejackets', required: true },
        { text: 'Muster crew and passengers', condition: 'vessel_pax', required: true },
        { text: 'Launch liferafts', condition: 'has_liferaft', required: true },
        { text: 'Activate EPIRB', condition: 'has_epirb', required: true },
        { text: 'Take grab bag and flares', required: true },
        { text: 'Account for all persons', required: true },
        { text: 'Last person - Master leaves vessel', required: true }
      ],
      reference: 'WBC3 Section 10.1.10, SOLAS'
    },

    // MEDICAL EMERGENCY
    {
      id: 'medical_emergency_v1',
      title: 'Medical Emergency',
      type: 'medical',
      icon: Heart,
      vesselTypes: ['all'],
      sortOrder: 6,
      duration: '20-30 min',
      tags: ['medical', 'emergency'],
      disclaimer: 'This checklist is a procedural aid, not a replacement for professional diagnosis or judgment. Based on Ship Captain\'s Medical Guide (24th ed).',
      steps: [
        { text: 'Ensure scene safety', required: true },
        { text: 'Check consciousness and breathing', required: true },
        { text: 'Call for assistance', required: true },
        { text: 'Control any bleeding', required: true },
        { text: 'Check for injuries', required: true },
        { text: 'Use SCMG or red-flag checklist', condition: 'med_stores_a_b', required: true },
        { text: 'Administer first aid as trained', required: true },
        { text: 'Contact medical advice (MEDICO)', condition: 'cat_0_3', required: false },
        { text: 'Consider medical evacuation', required: false },
        { text: 'Log all actions and times', required: true }
      ],
      reference: 'Ship Captain\'s Medical Guide'
    },

    // STEERING/NAVIGATION FAILURE
    {
      id: 'nav_failure_v1',
      title: 'Navigation/Steering Loss',
      type: 'navigation',
      icon: Navigation,
      vesselTypes: ['all'],
      sortOrder: 7,
      duration: '10-15 min',
      tags: ['navigation', 'emergency', 'recommended'],
      steps: [
        { text: 'Alert crew to steering failure', required: true },
        { text: 'Switch to emergency steering', condition: 'has_emergency_steering', required: true },
        { text: 'Reduce speed or stop engines', required: true },
        { text: 'Display NUC signals/lights', required: true },
        { text: 'Broadcast Securité message', condition: 'cat_0_4', required: true },
        { text: 'Check for traffic/hazards', required: true },
        { text: 'Deploy anchor if required', condition: 'safe_to_anchor', required: false },
        { text: 'Call for tug assistance', required: false },
        { text: 'Log failure and actions', required: true }
      ],
      reference: 'WBC3 Section 8.4'
    },

    // ELECTRICAL FAILURE
    {
      id: 'electrical_failure_v1',
      title: 'Electrical Failure',
      type: 'electrical',
      icon: Zap,
      vesselTypes: ['all'],
      minLengthM: 7,
      sortOrder: 8,
      duration: '10-15 min',
      tags: ['electrical', 'emergency', 'recommended'],
      steps: [
        { text: 'Check main breaker/fuses', required: true },
        { text: 'Switch to emergency power', condition: 'has_emergency_power', required: true },
        { text: 'Reduce electrical load', required: true },
        { text: 'Check battery voltage', required: true },
        { text: 'Start emergency generator', condition: 'has_generator', required: false },
        { text: 'Maintain navigation lights manually', required: true },
        { text: 'Use handheld VHF', condition: 'main_radio_failed', required: true },
        { text: 'Consider return to port', required: false },
        { text: 'Log failure and restoration', required: true }
      ],
      reference: 'WBC3 Section 9.3'
    },

    // EXPLOSION/GAS LEAK
    {
      id: 'gas_explosion_v1',
      title: 'Explosion/Gas Leak',
      type: 'explosion',
      icon: AlertCircle,
      vesselTypes: ['all'],
      sortOrder: 9,
      duration: '10-15 min',
      tags: ['gas', 'explosion', 'emergency', 'recommended'],
      disclaimer: 'For vessels with flammables, these steps are critical but must be reviewed with safety officer for specific fit.',
      steps: [
        { text: 'Evacuate affected area immediately', required: true },
        { text: 'Shut off gas supply', condition: 'has_gas_system', required: true },
        { text: 'Ventilate area - no electrical switches', required: true },
        { text: 'Check for injuries', required: true },
        { text: 'Do not use any ignition sources', required: true },
        { text: 'Use gas detector', condition: 'has_gas_detector', required: false },
        { text: 'Call emergency services', condition: 'in_port', required: true },
        { text: 'Consider abandon ship', condition: 'severe_explosion', required: false },
        { text: 'Log incident and damage', required: true }
      ],
      reference: 'WBC3 Section 10.1.9, MGN 543'
    },

    // POLLUTION INCIDENT
    {
      id: 'pollution_v1',
      title: 'Pollution Incident',
      type: 'pollution',
      icon: Droplet,
      vesselTypes: ['all'],
      sortOrder: 10,
      duration: '15-20 min',
      tags: ['pollution', 'emergency', 'recommended'],
      steps: [
        { text: 'Stop the source of pollution', required: true },
        { text: 'Contain spill if safe', required: true },
        { text: 'Deploy spill kit', condition: 'has_spill_kit', required: true },
        { text: 'Notify authorities immediately', required: true },
        { text: 'Log time, position, and quantity', required: true },
        { text: 'Take photos for evidence', required: false },
        { text: 'Do not use dispersants without approval', required: true },
        { text: 'Complete POLREP', required: true },
        { text: 'Notify P&I Club', condition: 'has_insurance', required: false }
      ],
      reference: 'MARPOL, MGN 553'
    },

    // DEFAULT FALLBACK
    {
      id: 'generic_emergency_v1',
      title: 'Generic Emergency Response',
      type: 'generic',
      icon: AlertTriangle,
      vesselTypes: ['all'],
      default: true,
      sortOrder: 999,
      duration: '10-15 min',
      tags: ['generic', 'emergency', 'fallback'],
      steps: [
        { text: 'Assess the situation', required: true },
        { text: 'Ensure crew safety', required: true },
        { text: 'Sound appropriate alarm', required: true },
        { text: 'Take immediate action to control', required: true },
        { text: 'Call for assistance if needed', required: true },
        { text: 'Log all actions taken', required: true }
      ],
      reference: 'Generic - Verify procedures for your vessel'
    }
  ]
};

/**
 * Get icon component for checklist type
 */
export const getChecklistIcon = (type) => {
  const iconMap = {
    fire: Flame,
    mob: Users,
    collision: AlertTriangle,
    flooding: Droplet,
    abandon: Anchor,
    medical: Heart,
    navigation: Navigation,
    electrical: Zap,
    explosion: AlertCircle,
    pollution: Droplet,
    generic: AlertTriangle
  };
  
  return iconMap[type] || AlertTriangle;
};